/**
 * @file Especializações numéricas Currency, Integer e Float.
 * @module components/create/number
 * @see module:components/create/input
 */

import { applyFullDefault } from './fullDefaults';

/**
 * Normalizador das especializações numéricas.
 *
 * @class
 */
class Number {
  /**
   * Configura `element: 'currency'`.
   *
   * O símbolo monetário é adicionado automaticamente em `prefix` ou `suffix`
   * conforme `dic.number.currencyPosition`, salvo quando o consumidor já
   * informou o respectivo adornment. Em seguida aplica o contrato de Float.
   *
   * @param {Object} options Opções recebidas pelo componente.
   * @param {number} [options.decimalPlaces] Casas decimais; usa `dic.number.decimalPlaces` quando ausente.
   * @returns {Object} O mesmo objeto `options` normalizado.
   * @see Number#float
   * @see module:components/create/input
   */
  currency (options){
    applyFullDefault(options);
    if (dic.number.currencyPosition === 'left') {
      if (options.prefix === undefined) {
        options.prefix = {
          text: dic.number.currencySymbol
        };
      }
    } else {
      if (options.suffix === undefined) {
        options.suffix = {
          text: dic.number.currencySymbol
        };
      }
    }
  
    return this.float(options);
  }

  /**
   * Configura `element: 'integer'` com máscara integer e zero casas decimais.
   *
   * @param {Object} options Opções recebidas pelo componente.
   * @returns {Object} O mesmo objeto `options` normalizado.
   * @see Number#float
   * @see module:components/create/input
   */
  integer(options) {
    applyFullDefault(options);
    options.mask = { template: 'integer' };
    options.decimalPlaces = 0;
    return this.float(options);
  }

  /**
   * Configura `element: 'float'` e a base usada por Currency/Integer.
   *
   * O input HTML é textual para permitir máscara. Quando não existe `mask`, usa
   * template `float`. `decimalPlaces` negativo é normalizado para zero e o
   * placeholder é derivado da quantidade final de casas.
   *
   * @param {Object} options Opções recebidas pelo componente.
   * @param {number} [options.decimalPlaces] Casas decimais; usa `dic.number.decimalPlaces` quando ausente.
   * @returns {Object} O mesmo objeto `options` normalizado.
   * @see module:components/create/input
   */
  float(options) {
    applyFullDefault(options);
    options.type = 'text';

    if (options.mask === undefined) {
      options.mask = { template: 'float' };
    }

    if (options.decimalPlaces !== undefined && typeof options.decimalPlaces === 'number') {
      options.mask.decimalPlaces = options.decimalPlaces;
    } else {
      options.mask.decimalPlaces = dic.number.decimalPlaces;
    }

    if (options.mask.decimalPlaces < 0) {
      options.mask.decimalPlaces = 0
    }

    if (options.mask.decimalPlaces === 0) {
      options.mask.placeholder = '0';
    } else {
      options.mask.placeholder = '0,' + '0'.repeat(options.mask.decimalPlaces);
    }

    return options;
  }
}

/** Instância compartilhada usada pelo pipeline Input. */
const number = new Number();
export default number;
